// /src/components/SSBComplianceRemediationAgent.jsx
import React, { useEffect, useMemo, useState } from "react";
import SSBReportShell from "./SSBReportShell";
import SSBFiltersBar from "./SSBFiltersBar";
import { getToken } from "../keycloak";

const API_BASE =
  process.env.REACT_APP_SSB_API_BASE ||
  (window.location.hostname === "localhost"
    ? "http://localhost:9191"
    : "https://lionfish-app-2-aehjc.ondigitalocean.app");

const STATUS = {
  NOT_STARTED: "Not Started",
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  UPCOMING: "Upcoming",
};

const FINDING_SEVERITY = ["Low", "Moderate", "High", "Critical"];
const FINDING_STATUS = ["Open", "In Progress", "Closed"];
const RECORD_TYPE = {
  FINDING: "FINDING",
  DECISION: "DECISION",
};

const MAX_ATTACHMENT_SIZE = 50 * 1024 * 1024;

const RISK_RATING_OPTIONS = [
  "High-Low",
  "High-Moderate",
  "High-High",
];

const REVIEW_FREQUENCY_OPTIONS = [
  "Yearly Transactional Review",
  "Bi-Yearly Transactional Review",
  "Quarterly Transactional Review",
];

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file selected"));
      return;
    }

    if (file.size <= 0) {
      reject(new Error("The selected file is empty"));
      return;
    }

    if (file.size > MAX_ATTACHMENT_SIZE) {
      reject(
        new Error("Attachment exceeds the 50 MB limit")
      );
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const result = String(reader.result || "");
      const commaIndex = result.indexOf(",");

      if (commaIndex < 0) {
        reject(
          new Error("Unable to encode attachment")
        );
        return;
      }

      const encoded = result.substring(
        commaIndex + 1
      );

      if (!encoded) {
        reject(
          new Error("Encoded attachment is empty")
        );
        return;
      }

      resolve(encoded);
    };

    reader.onerror = () => {
      reject(
        reader.error ||
          new Error("Failed to read attachment")
      );
    };

    reader.readAsDataURL(file);
  });

const Pill = ({ text, tone = "neutral" }) => {
  const map = {
    neutral: { bg: "#EEF2FF", fg: "#3730A3" },
    good: { bg: "#ECFDF3", fg: "#027A48" },
    warn: { bg: "#FFFAEB", fg: "#B54708" },
    bad: { bg: "#FEF3F2", fg: "#B42318" },
    info: { bg: "#EFF8FF", fg: "#175CD3" },
  };
  const c = map[tone] || map.neutral;

  return (
    <span
      style={{
        padding: "4px 10px",
        borderRadius: 999,
        background: c.bg,
        color: c.fg,
        fontSize: 12,
        fontWeight: 900,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
};

const statusTone = (status) => {
  if (status === STATUS.COMPLETED) return "good";
  if (status === STATUS.IN_PROGRESS) return "info";
  if (status === STATUS.UPCOMING) return "warn";
  return "neutral";
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const today = new Date();
  const due = new Date(dateStr);
  return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
};

const dueLabel = (dateStr) => {
  const days = daysUntil(dateStr);
  if (days === null) return "Not scheduled";
  if (days < 0) return `${Math.abs(days)} days overdue`;
  if (days === 0) return "Due today";
  return `Due in ${days} days`;
};

const dueTone = (dateStr) => {
  const days = daysUntil(dateStr);
  if (days === null) return "neutral";
  if (days < 0) return "bad";
  if (days <= 14) return "warn";
  return "good";
};

const formatExecutiveSummary = (text) => {
  if (!text) return "No summary provided.";

  return text
    .replace(/\s+/g, " ")
    .replace(/\. /g, ".\n\n")
    .trim();
};

const formatSummaryLines = (text) => {
  if (!text) return ["No summary provided."];

  return text
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
};

const formatSummaryBullets = (text) => {
  if (!text) return ["No summary provided."];

  return text
    .replace(/\s+/g, " ")
    .split(/(?<=\.)\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
};

const fetchRiskAssessments = async ({ programId }) => {
  const token = getToken();

  const resp = await fetch(
    `${API_BASE}/v1/ssb/risk-assessments?program_id=${programId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!resp.ok) {
    throw new Error("Failed to fetch risk assessments");
  }

  return resp.json();
};

const updateRiskAssessmentStatusApi = async ({ assessmentId, status }) => {
  const token = getToken();

  const resp = await fetch(
    `${API_BASE}/v1/ssb/risk-assessments/${assessmentId}/status?status=${encodeURIComponent(
      status
    )}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!resp.ok) {
    throw new Error("Failed to update risk assessment status");
  }

  return resp.json();
};

const updateRiskAssessmentApi = async ({ assessmentId, payload }) => {
  const token = getToken();

  const resp = await fetch(
    `${API_BASE}/v1/ssb/risk-assessments/${assessmentId}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );

  if (!resp.ok) {
    throw new Error("Failed to update risk assessment");
  }

  return resp.json();
};



const createRiskAssessmentApi = async ({ payload }) => {
  const token = getToken();

  const resp = await fetch(`${API_BASE}/v1/ssb/risk-assessments`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Failed to create risk assessment");
  }

  return resp.json();
};

const fetchProductsApi = async ({ programId }) => {
  const resp = await fetch(
    `${API_BASE}/v1/ssb/risk-assessments/products?program_id=${encodeURIComponent(
      programId
    )}`,
    {
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    }
  );

  const data = await resp.json().catch(() => ({}));

  if (!resp.ok) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : "Failed to load products"
    );
  }

  return data;
};


const createProductApi = async ({
  programId,
  productName,
}) => {
  const resp = await fetch(
    `${API_BASE}/v1/ssb/risk-assessments/products`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        program_id: Number(programId),
        product_name: productName,
        created_by: "SSB Compliance",
        updated_by: "SSB Compliance",
      }),
    }
  );

  const data = await resp.json().catch(() => ({}));

  if (!resp.ok) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : "Failed to add product"
    );
  }

  return data;
};


const deleteProductApi = async ({
  programId,
  productId,
}) => {
  const resp = await fetch(
    `${API_BASE}/v1/ssb/risk-assessments/products/${encodeURIComponent(
      programId
    )}/${encodeURIComponent(productId)}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    }
  );

  const data = await resp.json().catch(() => ({}));

  if (!resp.ok) {
    const error = new Error(
      typeof data.detail === "string"
        ? data.detail
        : "Failed to delete product"
    );

    error.status = resp.status;
    throw error;
  }

  return data;
};

const Card = ({ title, value, sub, tone = "neutral" }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #E7ECF3",
      borderRadius: 16,
      padding: 14,
      boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
      minHeight: 92,
    }}
  >
    <div style={{ fontSize: 12, color: "#64748B", fontWeight: 800 }}>
      {title}
    </div>
    <div
      style={{
        fontSize: 26,
        fontWeight: 950,
        color: "#0B1F33",
        marginTop: 6,
      }}
    >
      {value}
    </div>
    {sub ? (
      <div style={{ marginTop: 8 }}>
        <Pill text={sub} tone={tone} />
      </div>
    ) : null}
  </div>
);

const Section = ({ title, children, right }) => (
  <div
    style={{
      border: "1px solid #E7ECF3",
      borderRadius: 14,
      padding: 12,
      background: "#FFFFFF",
      marginBottom: 12,
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 10,
        marginBottom: 8,
      }}
    >
      <div style={{ fontWeight: 950, color: "#0B1F33" }}>{title}</div>
      {right}
    </div>
    {children}
  </div>
);
const fetchAssessmentFindings = async ({ assessmentId }) => {
  const resp = await fetch(`${API_BASE}/v1/ssb/risk-assessments/${assessmentId}/findings`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!resp.ok) throw new Error("Failed to fetch findings");
  return resp.json();
};

const createAssessmentFindingApi = async ({ assessmentId, payload }) => {
  const resp = await fetch(`${API_BASE}/v1/ssb/risk-assessments/${assessmentId}/findings`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) throw new Error("Failed to create finding");
  return resp.json();
};

const updateFindingStatusApi = async ({ findingId, status }) => {
  const resp = await fetch(`${API_BASE}/v1/ssb/risk-assessments/findings/${findingId}/status`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${getToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status, updated_by: "SSB Compliance" }),
  });
  if (!resp.ok) throw new Error("Failed to update finding");
  return resp.json();
};

const updateFindingApi = async ({ findingId, payload }) => {
  const resp = await fetch(
    `${API_BASE}/v1/ssb/risk-assessments/findings/${findingId}`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${getToken()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }
  );

  if (!resp.ok) throw new Error("Failed to update finding/decision");
  return resp.json();
};


const fetchAssessmentReminders = async ({ assessmentId }) => {
  const resp = await fetch(`${API_BASE}/v1/ssb/risk-assessments/${assessmentId}/reminders`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!resp.ok) throw new Error("Failed to fetch reminders");
  return resp.json();
};

const createAssessmentReminderApi = async ({ assessmentId, payload }) => {
  const resp = await fetch(`${API_BASE}/v1/ssb/risk-assessments/${assessmentId}/reminders`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) throw new Error("Failed to create reminder");
  return resp.json();
};

const updateReminderStatusApi = async ({ reminderId, status }) => {
  const resp = await fetch(`${API_BASE}/v1/ssb/risk-assessments/reminders/${reminderId}/status`, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${getToken()}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status, updated_by: "SSB Compliance" }),
  });
  if (!resp.ok) throw new Error("Failed to update reminder");
  return resp.json();
};

export default function SSBComplianceRemediationAgent() {
  const [programs, setPrograms] = useState([]);
  const [productsByProgram, setProductsByProgram] = useState({});

  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [selectedAssessmentId, setSelectedAssessmentId] = useState(null);

  const [assessments, setAssessments] = useState([]);
  const [notesByAssessment, setNotesByAssessment] = useState({});
  const [documentsByAssessment, setDocumentsByAssessment] = useState({});
  const [findingsByAssessment, setFindingsByAssessment] = useState({});
  const [remindersByAssessment, setRemindersByAssessment] = useState({});
  const [historyByAssessment, setHistoryByAssessment] = useState({});
  const [evidenceUploading, setEvidenceUploading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [editingAssessment, setEditingAssessment] = useState(false);
  const [assessmentDraft, setAssessmentDraft] = useState({});
  const [showCreateAssessment, setShowCreateAssessment] = useState(false);
  const [addingProduct, setAddingProduct] =
  useState(false);

    const [newProductName, setNewProductName] =
      useState("");

    const [savingProduct, setSavingProduct] =
      useState(false);

    const [deletingProductId, setDeletingProductId] =
      useState(null);
  const [deletingAttachmentId, setDeletingAttachmentId] =
  useState(null);
  const [createDraft, setCreateDraft] = useState({
    scope: "PROGRAM",
    product_id: "",
    assessment_type: "Program Risk Assessment",
    status: "Completed",
    cadence: "Yearly Transactional Review",
    assessment_date: "",
    next_due_date: "",
    owner: "SSB Compliance",
    risk_rating: "",
    assessment_score: "",
    assessment_summary: "",
  });
  const [newFinding, setNewFinding] = useState({
    record_type: RECORD_TYPE.FINDING,
    title: "",
    description: "",
    severity: "Moderate",
    status: "Open",
    owner: "SSB Compliance",
    target_date: "",
    decision_type: "Risk Acceptance",
    approver: "",
  });
  const [newReminderDays, setNewReminderDays] = useState(7);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [editingFindingId, setEditingFindingId] = useState(null);
  const [findingDraft, setFindingDraft] = useState({});

  useEffect(() => {
    let mounted = true;
  
    async function loadProgramsAndProducts() {
      setLoading(true);
      setErr("");
  
      try {
        const token = getToken();

        const resp = await fetch(`${API_BASE}/v1/ssb/risk-assessments/programs`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const json = await resp.json();
  
        const rawPrograms = Array.isArray(json.programs) ? json.programs : [];
        const programMap = new Map();
  
        rawPrograms.forEach((p) => {
          const row =
            typeof p === "string"
              ? {
                  program_id: p,
                  program_name: p,
                }
              : p;
  
          const programId = String(row.program_id);
  
          if (!programId || programId === "undefined" || programId === "null") {
            return;
          }
  
          if (!programMap.has(programId)) {
            programMap.set(programId, {
              ...row,
              program_id: row.program_id,
              program_name: row.program_name || `Program ${row.program_id}`,
            });
          }
        });
  
        const loadedPrograms = Array.from(programMap.values());
  
        if (!mounted) return;
  
        setPrograms(loadedPrograms);
        setProductsByProgram({});
  
        if (loadedPrograms.length > 0) {
          setSelectedProgramId(String(loadedPrograms[0].program_id));
        }
      } catch (e) {
        console.error(e);
        if (mounted) setErr("Failed to load programs.");
      } finally {
        if (mounted) setLoading(false);
      }
    }
  
    loadProgramsAndProducts();
  
    return () => {
      mounted = false;
    };
  }, []);

  

  const selectedProgram = useMemo(() => {
    return (
      programs.find((p) => String(p.program_id) === String(selectedProgramId)) ||
      null
    );
  }, [programs, selectedProgramId]);
  

  useEffect(() => {
  if (!selectedProgram?.program_id) {
    return;
  }

  const programId =
    selectedProgram.program_id;

  let mounted = true;

  async function loadProductsForSelectedProgram() {
    try {
      const data = await fetchProductsApi({
        programId,
      });

      if (!mounted) {
        return;
      }

      setProductsByProgram((prev) => ({
        ...prev,
        [String(programId)]:
          Array.isArray(data.products)
            ? data.products
            : [],
      }));
    } catch (error) {
      console.error(
        "Product load failed",
        error
      );

      if (mounted) {
        setProductsByProgram((prev) => ({
          ...prev,
          [String(programId)]: [],
        }));

        setErr(
          error.message ||
            "Failed to load products."
        );
      }
    }
  }

  loadProductsForSelectedProgram();

  return () => {
    mounted = false;
  };
}, [selectedProgram?.program_id]);

const refreshProducts = async (
  programId = selectedProgram?.program_id
) => {
  if (!programId) {
    return [];
  }

  const data = await fetchProductsApi({
    programId,
  });

  const products = Array.isArray(data.products)
    ? data.products
    : [];

  setProductsByProgram((prev) => ({
    ...prev,
    [String(programId)]: products,
  }));

  return products;
};


  useEffect(() => {
    if (!selectedProgram?.program_id) return;
   /*
    const subscriberId =
        selectedProgram.subscriber_id ||
        selectedProgram.subscriberId ||
        22;

        if (!subscriberId) {
          setAssessments([]);
          setSelectedAssessmentId(null);
          setErr(
            "Selected program is missing subscriber_id. Please ensure the filters API returns subscriber_id."
          );
          return;
        }*/

    let mounted = true;

    async function loadRiskAssessments() {
      setLoading(true);
      setErr("");
    
      try {
        const data = await fetchRiskAssessments({
          programId: selectedProgram.program_id,
        });
    
        if (!mounted) return;
    
        const rows = Array.isArray(data.assessments) ? data.assessments : [];
    
        setAssessments(rows);
    
        const programRow = rows.find((a) => a.scope === "PROGRAM");
        setSelectedAssessmentId(programRow?.id || rows[0]?.id || null);
      } catch (e) {
        console.error(e);
        if (mounted) {
          setErr("Failed to load risk assessments.");
          setAssessments([]);
          setSelectedAssessmentId(null);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadRiskAssessments();

    return () => {
      mounted = false;
    };
  }, [selectedProgram?.program_id]);

  

  const selectedAssessment = useMemo(() => {
    return assessments.find((a) => a.id === selectedAssessmentId) || null;
  }, [assessments, selectedAssessmentId]);

  const notes = selectedAssessment
    ? notesByAssessment[selectedAssessment.id] || []
    : [];
  const documents = selectedAssessment
    ? documentsByAssessment[selectedAssessment.id] || []
    : [];
  const findings = selectedAssessment
    ? findingsByAssessment[selectedAssessment.id] || []
    : [];
  const reminders = selectedAssessment
    ? remindersByAssessment[selectedAssessment.id] || []
    : [];
  const history = selectedAssessment
    ? historyByAssessment[selectedAssessment.id] || []
    : [];
  

    useEffect(() => {
      if (!selectedAssessment?.id) return;
    
      let mounted = true;
    
      async function loadNotes() {
        try {
          const data = await fetchAssessmentNotes({
            assessmentId: selectedAssessment.id,
          });
    
          if (!mounted) return;
    
          setNotesByAssessment((prev) => ({
            ...prev,
            [selectedAssessment.id]: data.notes || [],
          }));
        } catch (e) {
          console.error(e);
          setErr("Failed to load notes.");
        }
      }
    
      loadNotes();
    
      return () => {
        mounted = false;
      };
    }, [selectedAssessment?.id]);

    useEffect(() => {
      if (!selectedAssessment?.id) return;
    
      let mounted = true;
    
      async function loadAttachments() {
        try {
          const data = await fetchAssessmentAttachments({
            assessmentId: selectedAssessment.id,
          });
    
          if (!mounted) return;
    
          setDocumentsByAssessment((prev) => ({
            ...prev,
            [selectedAssessment.id]: data.attachments || [],
          }));
        } catch (e) {
          console.error(e);
          setErr("Failed to load evidence.");
        }
      }
    
      loadAttachments();
    
      return () => {
        mounted = false;
      };
    }, [selectedAssessment?.id]);

  
    useEffect(() => {
      if (!selectedAssessment?.id) return;
    
      let mounted = true;
    
      async function loadWorkItems() {
        try {
          const [findingsData, remindersData] = await Promise.all([
            fetchAssessmentFindings({
              assessmentId: selectedAssessment.id,
            }),
            fetchAssessmentReminders({
              assessmentId: selectedAssessment.id,
            }),
          ]);
    
          if (!mounted) return;
    
          setFindingsByAssessment((prev) => ({
            ...prev,
            [selectedAssessment.id]: findingsData.findings || [],
          }));
    
          setRemindersByAssessment((prev) => ({
            ...prev,
            [selectedAssessment.id]: remindersData.reminders || [],
          }));
        } catch (e) {
          console.error(e);
          setErr("Failed to load findings or action items.");
        }
      }
    
      loadWorkItems();
    
      return () => {
        mounted = false;
      };
    }, [selectedAssessment?.id]);
    
  const addHistory = (assessmentId, text) => {
    if (!assessmentId) return;

    const entry = {
      id: Date.now() + Math.random(),
      text,
      actor: "Compliance Officer",
      timestamp: new Date().toISOString(),
    };

    setHistoryByAssessment((prev) => ({
      ...prev,
      [assessmentId]: [entry, ...(prev[assessmentId] || [])],
    }));
  };

  const stats = useMemo(() => {
    const dueSoon = assessments.filter((a) => {
      const d = daysUntil(a.next_due_date);
      return d !== null && d >= 0 && d <= 30 && a.status !== STATUS.COMPLETED;
    }).length;

    const overdue = assessments.filter((a) => {
      const d = daysUntil(a.next_due_date);
      return d !== null && d < 0 && a.status !== STATUS.COMPLETED;
    }).length;

    const currentAssessmentIds = new Set(assessments.map((a) => a.id));

    const openFindings = Object.entries(findingsByAssessment).reduce(
      (sum, [assessmentId, arr]) => {
        if (!currentAssessmentIds.has(Number(assessmentId))) return sum;
    
        return (
          sum +
          (Array.isArray(arr)
            ? arr.filter(
                (x) =>
                  x.record_type === RECORD_TYPE.FINDING &&
                  x.status !== "Closed"
              ).length
            : 0)
        );
      },
      0
    );

const activeReminders = Object.entries(remindersByAssessment).reduce(
  (sum, [assessmentId, arr]) => {
    if (!currentAssessmentIds.has(Number(assessmentId))) return sum;

    return (
      sum +
      (Array.isArray(arr)
        ? arr.filter((x) => x.status !== "Closed").length
        : 0)
    );
  },
  0
);

    return {
      programs: programs.length,
      products: Object.values(productsByProgram).reduce(
        (sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0),
        0
      ),
      assessments: assessments.length,
      dueSoon,
      overdue,
      openFindings,
      activeReminders,
    };
  }, [
    programs,
    productsByProgram,
    assessments,
    findingsByAssessment,
    remindersByAssessment,
  ]);

  const handleProgramChange = (programId) => {
    setSelectedProgramId(programId);
    setSelectedAssessmentId(null);
  
    setAddingProduct(false);
    setNewProductName("");
    setSavingProduct(false);
    setDeletingProductId(null);
    setErr("");
  };
  
  const fetchAssessmentNotes = async ({ assessmentId }) => {
    const token = getToken();
  
    const resp = await fetch(
      `${API_BASE}/v1/ssb/risk-assessments/${assessmentId}/notes`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  
    if (!resp.ok) throw new Error("Failed to fetch notes");
    return resp.json();
  };
  
  const createAssessmentNoteApi = async ({ assessmentId, noteText }) => {
    const token = getToken();
  
    const resp = await fetch(
      `${API_BASE}/v1/ssb/risk-assessments/${assessmentId}/notes`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          note_text: noteText,
          created_by: "SSB Compliance",
          updated_by: "SSB Compliance",
        }),
      }
    );
  
    if (!resp.ok) throw new Error("Failed to create note");
    return resp.json();
  };

  const fetchAssessmentAttachments = async ({ assessmentId }) => {
    const token = getToken();
  
    const resp = await fetch(
      `${API_BASE}/v1/ssb/risk-assessments/${assessmentId}/attachments`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  
    if (!resp.ok) throw new Error("Failed to fetch attachments");
    return resp.json();
  };
  
  const createAssessmentAttachmentApi = async ({
    assessmentId,
    file,
  }) => {
    const token = getToken();
  
    const fileContentBase64 =
      await fileToBase64(file);
  
    const resp = await fetch(
      `${API_BASE}/v1/ssb/risk-assessments/${assessmentId}/attachments`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          file_name: file.name,
          file_type:
            file.type || "application/octet-stream",
          file_size: file.size,
          uploaded_by: "SSB Compliance",
          description: "Risk assessment evidence",
          file_content_base64:
            fileContentBase64,
          created_by: "SSB Compliance",
          updated_by: "SSB Compliance",
        }),
      }
    );
  
    if (!resp.ok) {
      const text = await resp.text();
  
      throw new Error(
        text || "Failed to upload evidence"
      );
    }
  
    return resp.json();
  };
  
  const deleteAssessmentAttachmentApi = async ({
    assessmentId,
    attachmentId,
  }) => {
    const response = await fetch(
      `${API_BASE}/v1/ssb/risk-assessments/${assessmentId}/attachments/${attachmentId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );
  
    if (!response.ok) {
      const text = await response.text();
  
      throw new Error(
        text || "Failed to delete attachment"
      );
    }
  
    return response.json();
  };
  const deleteEvidence = async (attachment) => {
    const assessmentId = selectedAssessment?.id;
    const attachmentId = attachment?.id;
  
    if (!assessmentId || !attachmentId) {
      return;
    }
  
    const confirmed = window.confirm(
      `Delete "${attachment.file_name || "this attachment"}"? This cannot be undone.`
    );
  
    if (!confirmed) {
      return;
    }
  
    try {
      setDeletingAttachmentId(attachmentId);
      setErr("");
  
      await deleteAssessmentAttachmentApi({
        assessmentId,
        attachmentId,
      });
  
      setDocumentsByAssessment((previous) => ({
        ...previous,
        [assessmentId]: (
          previous[assessmentId] || []
        ).filter(
          (document) =>
            document.id !== attachmentId
        ),
      }));
    } catch (error) {
      console.error(
        "Failed to delete attachment:",
        error
      );
  
      let message =
        error?.message ||
        "Failed to delete attachment.";
  
      try {
        const parsed = JSON.parse(message);
  
        if (parsed?.detail) {
          message =
            typeof parsed.detail === "string"
              ? parsed.detail
              : JSON.stringify(parsed.detail);
        }
      } catch {
        // Response was not JSON.
      }
  
      setErr(message);
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const fetchAssessmentActivity = async ({ assessmentId }) => {
    const token = getToken();
  
    const resp = await fetch(
      `${API_BASE}/v1/ssb/risk-assessments/${assessmentId}/activity`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
  
    if (!resp.ok) throw new Error("Failed to fetch activity");
    return resp.json();
  };
  const updateAssessmentStatus = async (status) => {
    if (!selectedAssessment) return;

    try {
      await updateRiskAssessmentStatusApi({
        assessmentId: selectedAssessment.id,
        status,
      });

      setAssessments((prev) =>
        prev.map((a) =>
          a.id === selectedAssessment.id
            ? { ...a, status, current_version: (a.current_version || 0) + 1 }
            : a
        )
      );

      addHistory(selectedAssessment.id, `Status changed to ${status}`);
    } catch (e) {
      console.error(e);
      setErr("Failed to update assessment status.");
    }
  };
  
  


  const addFinding = async () => {
  if (!selectedAssessment || !newFinding.title.trim()) return;

  try {
    const payload = {
      ...newFinding,
      title: newFinding.title.trim(),
      description: newFinding.description?.trim() || null,
      target_date: newFinding.target_date || null,
      created_by: "SSB Compliance",
      updated_by: "SSB Compliance",
    };

    await createAssessmentFindingApi({
      assessmentId: selectedAssessment.id,
      payload,
    });
    
    const data = await fetchAssessmentFindings({
      assessmentId: selectedAssessment.id,
    });
    
    console.log("FINDINGS RESPONSE", data);
    
    setFindingsByAssessment((prev) => ({
      ...prev,
      [selectedAssessment.id]: data.findings || data.items || data.data || [],
    }));

    const activity = await fetchAssessmentActivity({ assessmentId: selectedAssessment.id });
    setHistoryByAssessment((prev) => ({
      ...prev,
      [selectedAssessment.id]: activity.events || [],
    }));

    setNewFinding({
      record_type: RECORD_TYPE.FINDING,
      title: "",
      description: "",
      severity: "Moderate",
      status: "Open",
      owner: "SSB Compliance",
      target_date: "",
      decision_type: "Risk Acceptance",
      approver: "",
    });
  } catch (e) {
    console.error(e);
    setErr("Failed to add finding or decision.");
  }
};

const closeFinding = async (id) => {
  if (!selectedAssessment) return;

  try {
    await updateFindingStatusApi({ findingId: id, status: "Closed" });

    const data = await fetchAssessmentFindings({ assessmentId: selectedAssessment.id });

    setFindingsByAssessment((prev) => ({
      ...prev,
      [selectedAssessment.id]: data.findings || [],
    }));
    const activity = await fetchAssessmentActivity({
      assessmentId: selectedAssessment.id,
    });
    
    setHistoryByAssessment((prev) => ({
      ...prev,
      [selectedAssessment.id]: activity.events || [],
    }));
  } catch (e) {
    console.error(e);
    setErr("Failed to close finding.");
  }
};

  const addNote = async () => {
  if (!selectedAssessment || !newNote.trim()) return;

  const noteText = newNote.trim();

  try {
    const result = await createAssessmentNoteApi({
      assessmentId: selectedAssessment.id,
      noteText,
    });

    if (result.status !== "success") {
      throw new Error("Note create failed");
    }

    const data = await fetchAssessmentNotes({
      assessmentId: selectedAssessment.id,
    });
    
    setNotesByAssessment((prev) => ({
      ...prev,
      [selectedAssessment.id]: data.notes || [],
    }));
    addHistory(selectedAssessment.id, "Added note");
    setNewNote("");
  } catch (e) {
    console.error(e);
    setErr("Failed to add note.");
  }
};


const saveFindingEdit = async () => {
  if (!selectedAssessment || !editingFindingId) return;

  try {
    await updateFindingApi({
      findingId: editingFindingId,
      payload: {
        ...findingDraft,
        title: findingDraft.title?.trim(),
        description: findingDraft.description?.trim() || null,
        target_date: findingDraft.target_date || null,
        updated_by: "SSB Compliance",
      },
    });

    const data = await fetchAssessmentFindings({
      assessmentId: selectedAssessment.id,
    });

    setFindingsByAssessment((prev) => ({
      ...prev,
      [selectedAssessment.id]: data.findings || data.items || data.data || [],
    }));

    const activity = await fetchAssessmentActivity({
      assessmentId: selectedAssessment.id,
    });

    setHistoryByAssessment((prev) => ({
      ...prev,
      [selectedAssessment.id]: activity.events || [],
    }));

    setEditingFindingId(null);
    setFindingDraft({});
  } catch (e) {
    console.error(e);
    setErr("Failed to update finding or decision.");
  }
};


const addReminder = async () => {
  if (!selectedAssessment) return;

  const due = new Date();
  due.setDate(due.getDate() + Number(newReminderDays || 7));

  try {
    const result = await createAssessmentReminderApi({
      assessmentId: selectedAssessment.id,
      payload: {
        reminder_type: "Assessment Follow Up",
        due_date: due.toISOString().slice(0, 10),
        status: "Open",
        created_by: "SSB Compliance",
        updated_by: "SSB Compliance",
      },
    });

    if (result.status !== "success") throw new Error("Reminder create failed");

    const data = await fetchAssessmentReminders({ assessmentId: selectedAssessment.id });

    setRemindersByAssessment((prev) => ({
      ...prev,
      [selectedAssessment.id]: data.reminders || [],
    }));

    const activity = await fetchAssessmentActivity({ assessmentId: selectedAssessment.id });
    setHistoryByAssessment((prev) => ({
      ...prev,
      [selectedAssessment.id]: activity.events || [],
    }));
  } catch (e) {
    console.error(e);
    setErr("Failed to add action item.");
  }
};

const closeReminder = async (id) => {
  if (!selectedAssessment) return;

  try {
    await updateReminderStatusApi({ reminderId: id, status: "Closed" });

    const data = await fetchAssessmentReminders({ assessmentId: selectedAssessment.id });

    setRemindersByAssessment((prev) => ({
      ...prev,
      [selectedAssessment.id]: data.reminders || [],
    }));
    const activity = await fetchAssessmentActivity({
      assessmentId: selectedAssessment.id,
    });
    
    setHistoryByAssessment((prev) => ({
      ...prev,
      [selectedAssessment.id]: activity.events || [],
    }));
  } catch (e) {
    console.error(e);
    setErr("Failed to close action item.");
  }
};

const attachDocument = async (file) => {
  if (!selectedAssessment || !file) return;

  if (file.size <= 0) {
    setErr("The selected file is empty.");
    return;
  }

  if (file.size > MAX_ATTACHMENT_SIZE) {
    setErr(
      "Attachment exceeds the 50 MB limit."
    );
    return;
  }

  try {
    setEvidenceUploading(true);
    setErr("");

    const result =
      await createAssessmentAttachmentApi({
        assessmentId: selectedAssessment.id,
        file,
      });

    if (result.status !== "success") {
      throw new Error(
        "Evidence upload failed"
      );
    }

    const data =
      await fetchAssessmentAttachments({
        assessmentId: selectedAssessment.id,
      });

    setDocumentsByAssessment((prev) => ({
      ...prev,
      [selectedAssessment.id]:
        data.attachments || [],
    }));
  } catch (e) {
    console.error(e);

    setErr(
      e.message ||
        "Failed to upload evidence."
    );
  } finally {
    setEvidenceUploading(false);
  }
};

//ACB 07/25 added

const addProduct = async () => {
  const productName =
    newProductName.trim();

  if (!selectedProgram?.program_id) {
    setErr("Please select a program first.");
    return;
  }

  if (!productName) {
    setErr("Product name is required.");
    return;
  }

  try {
    setSavingProduct(true);
    setErr("");

    await createProductApi({
      programId:
        selectedProgram.program_id,
      productName,
    });

    await refreshProducts(
      selectedProgram.program_id
    );

    setNewProductName("");
    setAddingProduct(false);
  } catch (error) {
    console.error(
      "Failed to add product",
      error
    );

    setErr(
      error.message ||
        "Failed to add product."
    );
  } finally {
    setSavingProduct(false);
  }
};


const deleteProduct = async (product) => {
  if (
    !selectedProgram?.program_id ||
    !product?.product_id
  ) {
    return;
  }

  const confirmed = window.confirm(
    `Delete "${product.product_name}"?\n\nThis removes the product from the Risk Assessment registry.`
  );

  if (!confirmed) {
    return;
  }

  try {
    setDeletingProductId(
      product.product_id
    );
    setErr("");

    await deleteProductApi({
      programId:
        selectedProgram.program_id,
      productId:
        product.product_id,
    });

    await refreshProducts(
      selectedProgram.program_id
    );

    if (
      String(createDraft.product_id) ===
      String(product.product_id)
    ) {
      setCreateDraft((prev) => ({
        ...prev,
        product_id: "",
      }));
    }
  } catch (error) {
    console.error(
      "Failed to delete product",
      error
    );

    if (error.status === 409) {
      setErr(
        `"${product.product_name}" cannot be deleted because a risk assessment already exists.`
      );
    } else {
      setErr(
        error.message ||
          "Failed to delete product."
      );
    }
  } finally {
    setDeletingProductId(null);
  }
};

const viewEvidence = async (attachment) => {
  if (
    !selectedAssessment?.id ||
    !attachment?.id
  ) {
    return;
  }

  // Open synchronously so popup blockers allow it.
  // Do not pass noopener here because we need the window reference.
  const previewWindow = window.open("", "_blank");

  if (!previewWindow) {
    setErr(
      "The browser blocked the attachment window. Please allow pop-ups for this site."
    );
    return;
  }

  // Prevent the new tab from accessing the parent window.
  previewWindow.opener = null;

  previewWindow.document.write(`
    <html>
      <head>
        <title>Loading attachment...</title>
      </head>
      <body style="font-family: Arial, sans-serif; padding: 24px;">
        Loading attachment...
      </body>
    </html>
  `);

  previewWindow.document.close();

  try {
    setErr("");

    const resp = await fetch(
      `${API_BASE}/v1/ssb/risk-assessments/${selectedAssessment.id}/attachments/${attachment.id}/content`,
      {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      }
    );

    if (!resp.ok) {
      const text = await resp.text();

      throw new Error(
        text || "Failed to open attachment"
      );
    }

    const blob = await resp.blob();

    if (!blob.size) {
      throw new Error(
        "Attachment content is empty"
      );
    }

    const objectUrl = URL.createObjectURL(blob);

    previewWindow.location.replace(objectUrl);

    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 5 * 60 * 1000);
  } catch (e) {
    console.error(e);

    previewWindow.document.open();
    previewWindow.document.write(`
      <html>
        <head>
          <title>Attachment unavailable</title>
        </head>
        <body style="font-family: Arial, sans-serif; padding: 24px;">
          <h3>Unable to open attachment</h3>
          <p>${String(
            e.message || "Attachment could not be loaded"
          ).replace(/</g, "&lt;")}</p>
        </body>
      </html>
    `);
    previewWindow.document.close();

    setErr(
      e.message || "Unable to open this attachment."
    );
  }
};
  
  const saveNewAssessment = async () => {
    if (!selectedProgram?.program_id) {
      setErr("Please select a program first.");
      return;
    }
  
    if (!createDraft.assessment_type.trim()) {
      setErr("Assessment type is required.");
      return;
    }
  
    if (createDraft.scope === "PRODUCT" && !createDraft.product_id) {
      setErr("Please select a product for product assessment.");
      return;
    }
  
    try {
      const selectedProduct =
        createDraft.scope === "PRODUCT"
          ? (productsByProgram[String(selectedProgram.program_id)] || []).find(
              (p) => String(p.product_id) === String(createDraft.product_id)
            )
          : null;

      const payload = {
        scope: createDraft.scope,
        program_id: Number(selectedProgram.program_id),
        program_name: selectedProgram.program_name,
        product_id:
          createDraft.scope === "PRODUCT" ? Number(createDraft.product_id) : null,
        product_name: selectedProduct?.product_name || null,
        assessment_type: createDraft.assessment_type,
        status: createDraft.status,
        cadence: createDraft.cadence,
        assessment_date: createDraft.assessment_date || null,
        next_due_date: createDraft.next_due_date || createDraft.assessment_date || null,
        owner: createDraft.owner || "SSB Compliance",
        risk_rating: createDraft.risk_rating || null,
        assessment_score:
          createDraft.assessment_score === "" ||
          createDraft.assessment_score === null
            ? null
            : Number(createDraft.assessment_score),
        assessment_summary: createDraft.assessment_summary || null,
        created_by: "SSB Compliance",
        updated_by: "SSB Compliance",
      };
  
      const result = await createRiskAssessmentApi({ payload });
  
      if (result.status !== "success") {
        throw new Error("Assessment create failed");
      }
  
      setShowCreateAssessment(false);
  
      const data = await fetchRiskAssessments({
        programId: selectedProgram.program_id,
      });
  
      const rows = Array.isArray(data.assessments) ? data.assessments : [];
      setAssessments(rows);
      setSelectedAssessmentId(result.id || rows[0]?.id || null);
    } catch (e) {
      console.error(e);
      setErr("Failed to create assessment.");
    }
  };
  const createAssessment = () => {
    if (!selectedProgram?.program_id) {
      setErr("Please select a program first.");
      return;
    }
  
    setCreateDraft({
      scope: "PROGRAM",
      product_id: "",
      assessment_type: "Program Risk Assessment",
      status: "Completed",
      cadence: "Yearly Transactional Review",
      assessment_date: "",
      next_due_date: "",
      owner: "SSB Compliance",
      risk_rating: "",
      assessment_score: "",
      assessment_summary: "",
    });
  
    setShowCreateAssessment(true);
  };

  const programAssessment = assessments.find((a) => a.scope === "PROGRAM");

const productsForSelectedProgram =
  productsByProgram[String(selectedProgram?.program_id)] || [];

const productAssessments = productsForSelectedProgram.map((p) => {
  const assessment = assessments.find(
    (a) =>
      a.scope === "PRODUCT" &&
      String(a.product_id) === String(p.product_id)
  );

  return (
    assessment || {
      id: `product-${p.product_id}`,
      program_id: selectedProgram?.program_id,
      program_name: selectedProgram?.program_name,
      product_id: p.product_id,
      product_name: p.product_name,
      scope: "PRODUCT",
      assessment_type: "Product Risk Assessment",
      status: STATUS.NOT_STARTED,
      cadence: "Quarterly Transactional Review",
      risk_rating: "High-Moderate",
      next_due_date: null,
      assessment_score: null,
      assessment_summary: null,
      isPlaceholder: true,
    }
  );
});

  useEffect(() => {
    if (!selectedAssessment) {
      setAssessmentDraft({});
      setEditingAssessment(false);
      return;
    }
  
    setAssessmentDraft({
      assessment_type: selectedAssessment.assessment_type || "",
      status: selectedAssessment.status || STATUS.NOT_STARTED,
      owner: selectedAssessment.owner || "",
      cadence: selectedAssessment.cadence || "",
      next_due_date: selectedAssessment.next_due_date || "",
      risk_rating: selectedAssessment.risk_rating || "",
      assessment_summary: selectedAssessment.assessment_summary || "",
      assessment_score: selectedAssessment.assessment_score ?? "",
    });
  
    setEditingAssessment(false);
  }, [selectedAssessment?.id]);

  const saveAssessmentEdits = async () => {
    if (!selectedAssessment) return;
  
    try {
      const payload = {
        assessment_type: assessmentDraft.assessment_type || null,
        status: assessmentDraft.status || null,
        owner: assessmentDraft.owner || null,
        cadence: assessmentDraft.cadence || null,
        next_due_date: assessmentDraft.next_due_date || null,
        risk_rating: assessmentDraft.risk_rating || null,
        assessment_summary: assessmentDraft.assessment_summary || null,
        assessment_score:
          assessmentDraft.assessment_score === "" ||
          assessmentDraft.assessment_score === null
            ? null
            : Number(assessmentDraft.assessment_score),
        updated_by: "SSB Compliance",
      };
  
      const result = await updateRiskAssessmentApi({
        assessmentId: selectedAssessment.id,
        payload,
      });
  
      if (result.status !== "success") {
        throw new Error("Assessment update failed");
      }
  
      setAssessments((prev) =>
        prev.map((a) =>
          a.id === selectedAssessment.id
            ? {
                ...a,
                ...payload,
                current_version: (a.current_version || 0) + 1,
              }
            : a
        )
      );
  
      addHistory(selectedAssessment.id, "Updated assessment details");
      setEditingAssessment(false);
    } catch (e) {
      console.error(e);
      setErr("Failed to update assessment.");
    }
  };
  
  useEffect(() => {
    if (!selectedAssessment?.id) return;
  
    let mounted = true;
  
    async function loadActivity() {
      try {
        const data = await fetchAssessmentActivity({
          assessmentId: selectedAssessment.id,
        });
  
        if (!mounted) return;
  
        setHistoryByAssessment((prev) => ({
          ...prev,
          [selectedAssessment.id]: data.events || [],
        }));
      } catch (e) {
        console.error(e);
        setErr("Failed to load activity.");
      }
    }
  
    loadActivity();
  
    return () => {
      mounted = false;
    };
  }, [selectedAssessment?.id]);

  
  return (
    <SSBReportShell
      title="SSB Dashboard — Risk Assessment Module"
      subtitle="Program and product-level risk assessment registry, cadence tracking, findings, reminders, documents, and activity history"
      leftMeta={[
        "Sponsor bank oversight",
        "Risk assessment workflow",
        "Production module",
      ]}
      rightMeta={[loading ? "Loading…" : "Live", "MVP"]}
      footerLeft="Idenza • SSB Compliance Oversight"
      footerRight="Risk Assessment Module"
    >
      <SSBFiltersBar>
      <div style={{ minWidth: 420, flex: "0 0 420px" }}>
          <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 6 }}>
            Program
          </div>
          <select
            value={selectedProgramId}
            onChange={(e) => handleProgramChange(e.target.value)}
            disabled={loading || programs.length === 0}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 10,
              border: "1px solid #D7DEE9",
            }}
          >
            {programs.map((p) => (
            <option
              key={String(p.program_id)}
              value={String(p.program_id)}
            >
              {p.program_name}
            </option>
          ))}
          </select>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "end",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <Pill text={`Assessments: ${stats.assessments}`} tone="info" />
          <Pill text={`Due Soon: ${stats.dueSoon}`} tone="warn" />
          <Pill
            text={`Overdue: ${stats.overdue}`}
            tone={stats.overdue ? "bad" : "good"}
          />
          <Pill text={`Findings: ${stats.openFindings}`} tone="warn" />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "end",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={createAssessment}
            style={{
              padding: "9px 13px",
              borderRadius: 8,
              border: "none",
              background: "#1D4ED8",
              color: "#fff",
              fontWeight: 900,
              cursor: "pointer",
            }}
          >
            New Assessment
          </button>
        </div>
      </SSBFiltersBar>

      {err ? (
        <div
          style={{
            background: "#FEF3F2",
            border: "1px solid #FECACA",
            color: "#7F1D1D",
            padding: 12,
            borderRadius: 16,
            marginBottom: 12,
          }}
        >
          {err}
        </div>
      ) : null}
      {showCreateAssessment ? (
  <div
    style={{
      background: "#FFFFFF",
      border: "1px solid #E7ECF3",
      borderRadius: 16,
      padding: 14,
      marginBottom: 12,
      boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
    }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <div>
        <div style={{ fontWeight: 950, color: "#0B1F33", fontSize: 16 }}>
          New Risk Assessment
        </div>
        <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>
          Program: {selectedProgram?.program_name}
        </div>
      </div>

      <button
        onClick={() => setShowCreateAssessment(false)}
        style={{
          border: "1px solid #D7DEE9",
          background: "#fff",
          borderRadius: 8,
          padding: "6px 10px",
          fontWeight: 900,
          cursor: "pointer",
        }}
      >
        Cancel
      </button>
    </div>

    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gap: 10,
        marginTop: 12,
      }}
    >
      <select
        value={createDraft.scope}
        onChange={(e) =>
          setCreateDraft((prev) => ({
            ...prev,
            scope: e.target.value,
            product_id: "",
            assessment_type:
              e.target.value === "PROGRAM"
                ? "Program Risk Assessment"
                : "Product Risk Assessment",
          }))
        }
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      >
        <option value="PROGRAM">Program Assessment</option>
        <option value="PRODUCT">Product Assessment</option>
      </select>

      {createDraft.scope === "PRODUCT" ? (
        <select
          value={createDraft.product_id}
          onChange={(e) =>
            setCreateDraft((prev) => ({ ...prev, product_id: e.target.value }))
          }
          style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
        >
          <option value="">Select product</option>
          {(productsByProgram[String(selectedProgram?.program_id)] || []).map(
            (p) => (
              <option key={String(p.product_id)} value={String(p.product_id)}>
                {p.product_name}
              </option>
            )
          )}
        </select>
      ) : (
        <div
          style={{
            padding: 10,
            borderRadius: 10,
            border: "1px solid #E7ECF3",
            background: "#F8FAFC",
            color: "#64748B",
            fontSize: 13,
          }}
        >
          Applies to selected program
        </div>
      )}

      <input
        value={createDraft.assessment_type}
        onChange={(e) =>
          setCreateDraft((prev) => ({
            ...prev,
            assessment_type: e.target.value,
          }))
        }
        placeholder="Assessment type"
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />

      <select
        value={createDraft.status}
        onChange={(e) =>
          setCreateDraft((prev) => ({ ...prev, status: e.target.value }))
        }
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      >
        {Object.values(STATUS).map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>

      <select
          value={createDraft.cadence}
          onChange={(e) =>
            setCreateDraft((prev) => ({
              ...prev,
              cadence: e.target.value,
            }))
          }
          style={{
            padding: 10,
            borderRadius: 10,
            border: "1px solid #D7DEE9",
            background: "#FFFFFF",
          }}
        >
          <option value="">Select review frequency</option>

          {REVIEW_FREQUENCY_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>

      <input
        type="date"
        value={createDraft.assessment_date}
        onChange={(e) =>
          setCreateDraft((prev) => ({
            ...prev,
            assessment_date: e.target.value,
          }))
        }
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />

      <input
        type="date"
        value={createDraft.next_due_date}
        onChange={(e) =>
          setCreateDraft((prev) => ({
            ...prev,
            next_due_date: e.target.value,
          }))
        }
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />

      <input
        value={createDraft.owner}
        onChange={(e) =>
          setCreateDraft((prev) => ({ ...prev, owner: e.target.value }))
        }
        placeholder="Owner"
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />

<select
  value={createDraft.risk_rating}
  onChange={(e) =>
    setCreateDraft((prev) => ({
      ...prev,
      risk_rating: e.target.value,
    }))
  }
  style={{
    padding: 10,
    borderRadius: 10,
    border: "1px solid #D7DEE9",
    background: "#FFFFFF",
  }}
>
  <option value="">Select risk rating</option>

  {RISK_RATING_OPTIONS.map((option) => (
    <option key={option} value={option}>
      {option}
    </option>
  ))}
</select>

      <input
        type="number"
        step="0.01"
        value={createDraft.assessment_score}
        onChange={(e) =>
          setCreateDraft((prev) => ({
            ...prev,
            assessment_score: e.target.value,
          }))
        }
        placeholder="Score"
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />
    </div>

    <textarea
      value={createDraft.assessment_summary}
      onChange={(e) =>
        setCreateDraft((prev) => ({
          ...prev,
          assessment_summary: e.target.value,
        }))
      }
      placeholder="Assessment summary..."
      rows={4}
      style={{
        width: "100%",
        marginTop: 10,
        padding: 10,
        borderRadius: 10,
        border: "1px solid #D7DEE9",
        fontSize: 13,
      }}
    />

    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
      <button
        onClick={saveNewAssessment}
        style={{
          padding: "9px 13px",
          borderRadius: 8,
          border: "none",
          background: "#027A48",
          color: "#fff",
          fontWeight: 900,
          cursor: "pointer",
        }}
      >
        Create Assessment
      </button>
    </div>
  </div>
) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <Card title="Assessments" value={stats.assessments} sub="Registry" tone="info" />
        <Card title="Due Soon" value={stats.dueSoon} sub="Next 30 days" tone="warn" />
        <Card
          title="Overdue"
          value={stats.overdue}
          sub="Needs action"
          tone={stats.overdue ? "bad" : "good"}
        />
        <Card title="Open Findings" value={stats.openFindings} sub="Remediation" tone="warn" />
        <Card title="Reminders" value={stats.activeReminders} sub="Follow ups" tone="info" />
      </div>

      <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(620px, 1.2fr) minmax(650px, 0.8fr)",
              gap: 12,
              alignItems: "start",
            }}
          >
        <div
          style={{
            background: "#fff",
            border: "1px solid #E7ECF3",
            borderRadius: 16,
            padding: 14,
            boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <div>
              <div style={{ fontWeight: 950, color: "#0B1F33", fontSize: 16 }}>
              Program Risk Portfolio
              </div>
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>
                {selectedProgram?.program_name || "Select program"}
              </div>
            </div>
            <Pill
              text={loading ? "Loading…" : "Risk Governance Workflow"}
              tone={loading ? "warn" : "good"}
            />
          </div>

          {programAssessment ? (
            <Section
              title="Program-Risk Assessment"
              right={
                <Pill
                  text={programAssessment.status}
                  tone={statusTone(programAssessment.status)}
                />
              }
            >
              <div
                onClick={() => setSelectedAssessmentId(programAssessment.id)}
                style={{
                  cursor: "pointer",
                  border:
                    selectedAssessmentId === programAssessment.id
                      ? "2px solid #93C5FD"
                      : "1px solid #F1F5F9",
                  borderRadius: 12,
                  padding: 12,
                  background:
                    selectedAssessmentId === programAssessment.id
                      ? "#EFF6FF"
                      : "#FBFDFF",
                }}
              >
                <div style={{ fontWeight: 950, color: "#0B1F33" }}>
                  {programAssessment.assessment_type}
                </div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 4 }}>
                  Cadence: {programAssessment.cadence} • Due:{" "}
                  {programAssessment.next_due_date || "Not scheduled"} • Owner:{" "}
                  {programAssessment.owner}
                </div> <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                    <Pill
                      text={`Risk: ${programAssessment.risk_rating || "Not rated"}`}
                      tone="warn"
                    />
                    <Pill
                      text={`Score: ${programAssessment.assessment_score ?? "N/A"}`}
                      tone="info"
                    />
                    <Pill
                      text={`Version: ${programAssessment.current_version || 1}`}
                      tone="neutral"
                    />
                    <Pill
  text={`Open Findings: ${
    findings.filter(
      (x) =>
        x.record_type === RECORD_TYPE.FINDING &&
        x.status !== "Closed"
    ).length  }`}
  tone="warn"
/>
                  </div>
                                </div>
            </Section>
          ) : null}

<Section
  title="Product-Level Assessments"
  right={
    <Pill
      text={`${productAssessments.length} products`}
      tone="neutral"
    />
  }
>
  <div
    style={{
      overflowX: "auto",
      border: "1px solid #F1F5F9",
      borderRadius: 12,
    }}
  >
    <table
      style={{
        width: "100%",
        borderCollapse: "collapse",
        fontSize: 13,
      }}
    >
      <thead>
        <tr style={{ background: "#F8FAFC" }}>
          {[
            "Product",
            "Status",
            "Cadence",
            "Next Due",
            "Risk",
            "Findings",
            "Docs",
            "Actions",
          ].map((heading) => (
            <th
              key={heading}
              style={{
                padding: 10,
                textAlign: "left",
                fontWeight: 900,
                color: "#334155",
              }}
            >
              {heading}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {productAssessments.map((assessment) => {
          const isSelected =
            selectedAssessmentId ===
            assessment.id;

          const productFindings =
            findingsByAssessment[
              assessment.id
            ] || [];

          const productDocuments =
            documentsByAssessment[
              assessment.id
            ] || [];

          return (
            <tr
              key={assessment.id}
              onClick={() => {
                if (assessment.isPlaceholder) {
                  setCreateDraft({
                    scope: "PRODUCT",
                    product_id: String(
                      assessment.product_id
                    ),
                    assessment_type:
                      "Product Risk Assessment",
                    status:
                      STATUS.NOT_STARTED,
                    cadence:
                      "Quarterly Transactional Review",
                    risk_rating:
                      assessment.risk_rating ||
                      "High-Moderate",
                    assessment_date: "",
                    next_due_date: "",
                    owner:
                      "SSB Compliance",
                    assessment_score: "",
                    assessment_summary: "",
                  });

                  setShowCreateAssessment(true);
                  return;
                }

                setSelectedAssessmentId(
                  assessment.id
                );
              }}
              style={{
                borderTop:
                  "1px solid #F1F5F9",
                background: isSelected
                  ? "#EFF6FF"
                  : "#FFFFFF",
                cursor: "pointer",
              }}
            >
              <td
                style={{
                  padding: 10,
                  fontWeight: 900,
                  color: "#0B1F33",
                }}
              >
                {assessment.product_name}
              </td>

              <td style={{ padding: 10 }}>
                <Pill
                  text={assessment.status}
                  tone={statusTone(
                    assessment.status
                  )}
                />
              </td>

              <td style={{ padding: 10 }}>
                {assessment.cadence}
              </td>

              <td style={{ padding: 10 }}>
                <Pill
                  text={dueLabel(
                    assessment.next_due_date
                  )}
                  tone={dueTone(
                    assessment.next_due_date
                  )}
                />
              </td>

              <td style={{ padding: 10 }}>
                {assessment.risk_rating}
              </td>

              <td style={{ padding: 10 }}>
                {
                  productFindings.filter(
                    (finding) =>
                      finding.status !==
                      "Closed"
                  ).length
                }
              </td>

              <td style={{ padding: 10 }}>
                {productDocuments.length}
              </td>

              <td style={{ padding: 10 }}>
                <div
                  style={{
                    display: "flex",
                    gap: 6,
                    alignItems: "center",
                    whiteSpace: "nowrap",
                  }}
                >
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();

                      if (
                        assessment.isPlaceholder
                      ) {
                        setCreateDraft({
                          scope: "PRODUCT",
                          product_id: String(
                            assessment.product_id
                          ),
                          assessment_type:
                            "Product Risk Assessment",
                          status:
                            STATUS.NOT_STARTED,
                          cadence:
                            "Quarterly Transactional Review",
                          risk_rating:
                            assessment.risk_rating ||
                            "High-Moderate",
                          assessment_date: "",
                          next_due_date: "",
                          owner:
                            "SSB Compliance",
                          assessment_score: "",
                          assessment_summary: "",
                        });

                        setShowCreateAssessment(
                          true
                        );
                        return;
                      }

                      setSelectedAssessmentId(
                        assessment.id
                      );
                    }}
                    style={{
                      padding: "6px 9px",
                      borderRadius: 8,
                      border:
                        "1px solid #BFDBFE",
                      background: "#EFF6FF",
                      color: "#1D4ED8",
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    {assessment.isPlaceholder
                      ? "Create"
                      : "Open"}
                  </button>

                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();

                      deleteProduct({
                        product_id:
                          assessment.product_id,
                        product_name:
                          assessment.product_name,
                      });
                    }}
                    disabled={
                      deletingProductId ===
                      assessment.product_id
                    }
                    style={{
                      padding: "6px 9px",
                      borderRadius: 8,
                      border:
                        "1px solid #FECACA",
                      background: "#FEF2F2",
                      color: "#B91C1C",
                      fontWeight: 800,
                      cursor:
                        deletingProductId ===
                        assessment.product_id
                          ? "not-allowed"
                          : "pointer",
                      opacity:
                        deletingProductId ===
                        assessment.product_id
                          ? 0.6
                          : 1,
                    }}
                  >
                    {deletingProductId ===
                    assessment.product_id
                      ? "Deleting..."
                      : "Delete"}
                  </button>
                </div>
              </td>
            </tr>
          );
        })}

        {addingProduct ? (
          <tr
            style={{
              borderTop:
                "1px solid #F1F5F9",
              background: "#F8FAFC",
            }}
          >
            <td
              colSpan={7}
              style={{ padding: 10 }}
            >
              <input
                value={newProductName}
                onChange={(event) =>
                  setNewProductName(
                    event.target.value
                  )
                }
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" &&
                    !savingProduct &&
                    newProductName.trim()
                  ) {
                    addProduct();
                  }

                  if (
                    event.key === "Escape" &&
                    !savingProduct
                  ) {
                    setAddingProduct(false);
                    setNewProductName("");
                  }
                }}
                maxLength={255}
                autoFocus
                disabled={savingProduct}
                placeholder="Enter product name"
                style={{
                  width: "100%",
                  padding: 10,
                  borderRadius: 8,
                  border:
                    "1px solid #D7DEE9",
                  background: "#FFFFFF",
                }}
              />
            </td>

            <td style={{ padding: 10 }}>
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  whiteSpace: "nowrap",
                }}
              >
                <button
                  type="button"
                  onClick={addProduct}
                  disabled={
                    savingProduct ||
                    !newProductName.trim()
                  }
                  style={{
                    padding: "6px 9px",
                    borderRadius: 8,
                    border: "none",
                    background: "#027A48",
                    color: "#FFFFFF",
                    fontWeight: 800,
                    cursor:
                      savingProduct ||
                      !newProductName.trim()
                        ? "not-allowed"
                        : "pointer",
                    opacity:
                      savingProduct ||
                      !newProductName.trim()
                        ? 0.6
                        : 1,
                  }}
                >
                  {savingProduct
                    ? "Adding..."
                    : "Save"}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setAddingProduct(false);
                    setNewProductName("");
                  }}
                  disabled={savingProduct}
                  style={{
                    padding: "6px 9px",
                    borderRadius: 8,
                    border:
                      "1px solid #D7DEE9",
                    background: "#FFFFFF",
                    color: "#475569",
                    fontWeight: 800,
                    cursor: savingProduct
                      ? "not-allowed"
                      : "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </td>
          </tr>
        ) : (
          <tr
            style={{
              borderTop:
                "1px solid #F1F5F9",
            }}
          >
            <td
              colSpan={8}
              style={{ padding: 10 }}
            >
              <button
                type="button"
                onClick={() => {
                  setErr("");
                  setNewProductName("");
                  setAddingProduct(true);
                }}
                disabled={!selectedProgram}
                style={{
                  padding: "7px 10px",
                  borderRadius: 8,
                  border:
                    "1px dashed #93C5FD",
                  background: "#EFF6FF",
                  color: "#1D4ED8",
                  fontWeight: 900,
                  cursor: selectedProgram
                    ? "pointer"
                    : "not-allowed",
                  opacity: selectedProgram
                    ? 1
                    : 0.6,
                }}
              >
                + Add Product
              </button>
            </td>
          </tr>
        )}

        {!loading &&
        productAssessments.length === 0 &&
        !addingProduct ? (
          <tr>
            <td
              colSpan={8}
              style={{
                padding: 12,
                color: "#64748B",
              }}
            >
              No products found for this program.
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  </div>
</Section>
        </div>

        <div
          style={{
            background: "#fff",
            border: "1px solid #E7ECF3",
            borderRadius: 16,
            padding: 14,
            boxShadow: "0 10px 30px rgba(16,24,40,0.06)",
            height: "calc(100vh - 120px)",
            maxHeight: "calc(100vh - 120px)",
            overflowY: "auto",
            minWidth: 460,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 12,
            }}
          >
            <div>
              <div style={{ fontWeight: 950, color: "#0B1F33", fontSize: 16 }}>
                Assessment Detail
              </div>
              <div style={{ fontSize: 12, color: "#64748B", marginTop: 3 }}>
                {selectedAssessment?.product_name ||
                  selectedAssessment?.program_name ||
                  "Select assessment"}
              </div>
            </div>
            {selectedAssessment ? (
              <Pill
                text={selectedAssessment.status}
                tone={statusTone(selectedAssessment.status)}
              />
            ) : null}
          </div>

          {selectedAssessment ? (
  <>
    <Section
      title="Executive Risk Summary"
      right={<Pill text={selectedAssessment.scope} tone="info" />}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 10 }}>
  <div style={{ fontSize: 13, color: "#64748B" }}>
    Core assessment metadata and current risk posture.
  </div>

  {editingAssessment ? (
    <div style={{ display: "flex", gap: 8 }}>
      <button
        onClick={saveAssessmentEdits}
        style={{
          padding: "6px 9px",
          borderRadius: 8,
          border: "none",
          background: "#027A48",
          color: "#fff",
          fontWeight: 900,
          cursor: "pointer",
        }}
      >
        Save
      </button>
      <button
        onClick={() => setEditingAssessment(false)}
        style={{
          padding: "6px 9px",
          borderRadius: 8,
          border: "1px solid #D7DEE9",
          background: "#fff",
          color: "#475569",
          fontWeight: 900,
          cursor: "pointer",
        }}
      >
        Cancel
      </button>
    </div>
  ) : (
    <button
      onClick={() => setEditingAssessment(true)}
      style={{
        padding: "6px 9px",
        borderRadius: 8,
        border: "1px solid #BFDBFE",
        background: "#EFF6FF",
        color: "#1D4ED8",
        fontWeight: 900,
        cursor: "pointer",
      }}
    >
      Edit
    </button>
  )}
</div>

{editingAssessment ? (
  <div style={{ display: "grid", gap: 10 }}>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      <input
        value={assessmentDraft.assessment_type || ""}
        onChange={(e) =>
          setAssessmentDraft((prev) => ({
            ...prev,
            assessment_type: e.target.value,
          }))
        }
        placeholder="Assessment Type"
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />

      <select
        value={assessmentDraft.status || STATUS.NOT_STARTED}
        onChange={(e) =>
          setAssessmentDraft((prev) => ({
            ...prev,
            status: e.target.value,
          }))
        }
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      >
        {Object.values(STATUS).map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
    </div>

    <textarea
  value={assessmentDraft.assessment_summary || ""}
  onChange={(e) =>
    setAssessmentDraft((prev) => ({
      ...prev,
      assessment_summary: e.target.value,
    }))
  }
  placeholder="Executive summary..."
  rows={8}
  style={{
    width: "100%",
    padding: 12,
    borderRadius: 12,
    border: "1px solid #D7DEE9",
    fontSize: 14,
    lineHeight: 1.65,
    whiteSpace: "pre-wrap",
    overflowWrap: "break-word",
    resize: "vertical",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  }}
/>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      <input
        value={assessmentDraft.owner || ""}
        onChange={(e) =>
          setAssessmentDraft((prev) => ({ ...prev, owner: e.target.value }))
        }
        placeholder="Owner"
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />

<select
  value={assessmentDraft.cadence || ""}
  onChange={(e) =>
    setAssessmentDraft((prev) => ({
      ...prev,
      cadence: e.target.value,
    }))
  }
  style={{
    padding: 10,
    borderRadius: 10,
    border: "1px solid #D7DEE9",
    background: "#FFFFFF",
  }}
>
  <option value="">Select review frequency</option>

  {REVIEW_FREQUENCY_OPTIONS.map((option) => (
    <option key={option} value={option}>
      {option}
    </option>
  ))}
</select>

      <input
        type="date"
        value={assessmentDraft.next_due_date || ""}
        onChange={(e) =>
          setAssessmentDraft((prev) => ({
            ...prev,
            next_due_date: e.target.value,
          }))
        }
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />

<select
  value={assessmentDraft.risk_rating || ""}
  onChange={(e) =>
    setAssessmentDraft((prev) => ({
      ...prev,
      risk_rating: e.target.value,
    }))
  }
  style={{
    padding: 10,
    borderRadius: 10,
    border: "1px solid #D7DEE9",
    background: "#FFFFFF",
  }}
>
  <option value="">Select risk rating</option>

  {RISK_RATING_OPTIONS.map((option) => (
    <option key={option} value={option}>
      {option}
    </option>
  ))}
</select>

      <input
        type="number"
        step="0.01"
        value={assessmentDraft.assessment_score || ""}
        onChange={(e) =>
          setAssessmentDraft((prev) => ({
            ...prev,
            assessment_score: e.target.value,
          }))
        }
        placeholder="Score"
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />
    </div>
  </div>
) : (
  <div style={{ display: "grid", gap: 12 }}>
  <div style={{ fontSize: 15, fontWeight: 950, color: "#0B1F33" }}>
    {selectedAssessment.assessment_type}
  </div>

  <div
  style={{
    background: "#F8FAFC",
    border: "1px solid #E7ECF3",
    borderRadius: 12,
    padding: 14,
    color: "#334155",
    fontSize: 14,
    lineHeight: 1.65,
    overflowWrap: "break-word",
  }}
>
  <div style={{ display: "grid", gap: 6 }}>
    {formatSummaryLines(selectedAssessment.assessment_summary).map((line, idx) => {
      const isHeader =
        !line.startsWith("•") &&
        !line.startsWith("-") &&
        !line.includes(":");

      return (
        <div
          key={idx}
          style={{
            fontWeight: isHeader ? 900 : 500,
            marginTop: isHeader && idx > 0 ? 10 : 0,
          }}
        >
          {line}
        </div>
      );
    })}
  </div>
</div>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 8,
    }}
  >
    {[
      ["Program", selectedAssessment.program_name],
      ["Product", selectedAssessment.product_name || "Program Level"],
      ["Status", selectedAssessment.status],
      ["Cadence", selectedAssessment.cadence],
      [
        "Next Review",
        `${selectedAssessment.next_due_date || "Not scheduled"}${
          selectedAssessment.next_due_date
            ? ` (${dueLabel(selectedAssessment.next_due_date)})`
            : ""
        }`,
      ],
      ["Owner", selectedAssessment.owner || "Unassigned"],
      ["Risk", selectedAssessment.risk_rating || "Not rated"],
      [
        "Score",
        selectedAssessment.assessment_score ?? "Not scored",
      ],
      ["Version", selectedAssessment.current_version || 1],
    ].map(([label, value]) => (
      <div
        key={label}
        style={{
          background: "#FFFFFF",
          border: "1px solid #E7ECF3",
          borderRadius: 10,
          padding: 10,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: "#64748B",
            fontWeight: 900,
          }}
        >
          {label}
        </div>

        <div
          style={{
            fontSize: 13,
            color: "#0B1F33",
            fontWeight: 850,
          }}
        >
          {value || "—"}
        </div>
      </div>
    ))}
  </div>
</div>
)}

<div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
  {Object.values(STATUS).map((s) => (
    <button
      key={s}
      onClick={() => updateAssessmentStatus(s)}
      style={{
        padding: "6px 9px",
        borderRadius: 8,
        border:
          selectedAssessment.status === s
            ? "1px solid #3730A3"
            : "1px solid #D7DEE9",
        background: selectedAssessment.status === s ? "#EEF2FF" : "#FFFFFF",
        color: selectedAssessment.status === s ? "#3730A3" : "#475569",
        fontWeight: 800,
        cursor: "pointer",
      }}
    >
      {s}
    </button>
  ))}
</div>
    </Section>

    <Section
  title="Findings & Decisions"
  right={
    <div style={{ display: "flex", gap: 6 }}>
      <Pill
        text={`${
          findings.filter((x) => x.record_type === RECORD_TYPE.FINDING).length
        } Findings`}
        tone="warn"
      />
      <Pill
        text={`${
          findings.filter((x) => x.record_type === RECORD_TYPE.DECISION).length
        } Decisions`}
        tone="info"
      />
    </div>
  }
>
  {findings.length === 0 ? (
    <div style={{ color: "#64748B", fontSize: 13, marginBottom: 10 }}>
      No findings or decisions yet.
    </div>
  ) : (
    findings.map((item) => (
      <div
        key={item.id}
        style={{
          border: "1px solid #F1F5F9",
          borderRadius: 10,
          padding: 10,
          marginBottom: 8,
          background: item.status === "Closed" ? "#F8FAFC" : "#FFFFFF",
        }}
      >
       {editingFindingId === item.id ? (
  <div style={{ display: "grid", gap: 10 }}>
    <div>
      <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", marginBottom: 4 }}>
        Title
      </div>
      <input
        value={findingDraft.title || ""}
        onChange={(e) => setFindingDraft((prev) => ({ ...prev, title: e.target.value }))}
        style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />
    </div>

    <div>
      <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", marginBottom: 4 }}>
        Description
      </div>
      <textarea
        rows={4}
        value={findingDraft.description || ""}
        onChange={(e) => setFindingDraft((prev) => ({ ...prev, description: e.target.value }))}
        style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      {findingDraft.record_type === RECORD_TYPE.FINDING ? (
        <div>
          <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", marginBottom: 4 }}>
            Severity
          </div>
          <select
            value={findingDraft.severity || "Moderate"}
            onChange={(e) => setFindingDraft((prev) => ({ ...prev, severity: e.target.value }))}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          >
            {FINDING_SEVERITY.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", marginBottom: 4 }}>
            Decision Type
          </div>
          <input
            value={findingDraft.decision_type || ""}
            onChange={(e) => setFindingDraft((prev) => ({ ...prev, decision_type: e.target.value }))}
            style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
          />
        </div>
      )}

      <div>
        <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", marginBottom: 4 }}>
          Status
        </div>
        <select
          value={findingDraft.status || "Open"}
          onChange={(e) => setFindingDraft((prev) => ({ ...prev, status: e.target.value }))}
          style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
        >
          {FINDING_STATUS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </div>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", marginBottom: 4 }}>
          Owner
        </div>
        <input
          value={findingDraft.owner || ""}
          onChange={(e) => setFindingDraft((prev) => ({ ...prev, owner: e.target.value }))}
          style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
        />
      </div>

      <div>
        <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", marginBottom: 4 }}>
          Resolution Due
        </div>
        <input
          type="date"
          value={findingDraft.target_date || ""}
          onChange={(e) => setFindingDraft((prev) => ({ ...prev, target_date: e.target.value }))}
          style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
        />
      </div>
    </div>

    {findingDraft.record_type === RECORD_TYPE.DECISION ? (
      <div>
        <div style={{ fontSize: 11, fontWeight: 900, color: "#64748B", marginBottom: 4 }}>
          Approver
        </div>
        <input
          value={findingDraft.approver || ""}
          onChange={(e) => setFindingDraft((prev) => ({ ...prev, approver: e.target.value }))}
          style={{ width: "100%", padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
        />
      </div>
    ) : null}

    <div style={{ display: "flex", gap: 8 }}>
      <button
        onClick={saveFindingEdit}
        style={{
          padding: "6px 10px",
          borderRadius: 8,
          border: "none",
          background: "#027A48",
          color: "#fff",
          fontWeight: 900,
          cursor: "pointer",
        }}
      >
        Save
      </button>

      <button
        onClick={() => {
          setEditingFindingId(null);
          setFindingDraft({});
        }}
        style={{
          padding: "6px 10px",
          borderRadius: 8,
          border: "1px solid #D7DEE9",
          background: "#fff",
          cursor: "pointer",
        }}
      >
        Cancel
      </button>
    </div>
  </div>
) : (
        
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <div>
            <div style={{ display: "flex", gap: 6, marginBottom: 5 }}>
              <Pill
                text={item.record_type === RECORD_TYPE.DECISION ? "Decision" : "Finding"}
                tone={item.record_type === RECORD_TYPE.DECISION ? "info" : "warn"}
              />
              {item.record_type === RECORD_TYPE.FINDING ? (
                <Pill text={`Severity: ${item.severity}`} tone="neutral" />
              ) : (
                <Pill text={item.decision_type || "Decision"} tone="info" />
              )}
              <Pill
                text={item.status}
                tone={item.status === "Closed" ? "good" : "warn"}
              />
            </div>

            <div style={{ fontSize: 13, fontWeight: 900, color: "#0B1F33" }}>
              {item.title || item.text}
            </div>

            {item.description ? (
              <div style={{ fontSize: 12, color: "#475569", marginTop: 5 }}>
                {item.description}
              </div>
            ) : null}

                <div style={{ fontSize: 11, color: "#64748B", marginTop: 5 }}>
                  Owner: {item.owner || "Unassigned"}
                </div>

                {item.target_date ? (
                  <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
                    Resolution Due: {item.target_date}
                  </div>
                ) : null}

                {item.approver ? (
                  <div style={{ fontSize: 11, color: "#64748B", marginTop: 2 }}>
                    Approver: {item.approver}
                  </div>
                ) : null}
                          </div>

          <div style={{ display: "flex", gap: 6 }}>
  <button
    onClick={() => {
      setEditingFindingId(item.id);
      setFindingDraft({
        ...item,
        target_date: item.target_date || "",
      });
    }}
    style={{
      padding: "5px 8px",
      borderRadius: 8,
      border: "1px solid #BFDBFE",
      background: "#EFF6FF",
      color: "#1D4ED8",
      fontWeight: 800,
      cursor: "pointer",
      height: 30,
    }}
  >
    Edit
  </button>

  {item.status === "Closed" ? null : (
    <button
      onClick={() => closeFinding(item.id)}
      style={{
        padding: "5px 8px",
        borderRadius: 8,
        border: "1px solid #BBF7D0",
        background: "#ECFDF3",
        color: "#027A48",
        fontWeight: 800,
        cursor: "pointer",
        height: 30,
      }}
    >
      Close
    </button>
  )}
</div>
        </div>
        )}
      </div>
    ))
  )}

  <div style={{ display: "grid", gap: 8 }}>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      <select
        value={newFinding.record_type}
        onChange={(e) =>
          setNewFinding((prev) => ({
            ...prev,
            record_type: e.target.value,
          }))
        }
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      >
        <option value={RECORD_TYPE.FINDING}>Finding</option>
        <option value={RECORD_TYPE.DECISION}>Decision</option>
      </select>

      <select
        value={newFinding.status}
        onChange={(e) =>
          setNewFinding((prev) => ({ ...prev, status: e.target.value }))
        }
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      >
        {FINDING_STATUS.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
    </div>

    <input
      value={newFinding.title}
      onChange={(e) =>
        setNewFinding((prev) => ({ ...prev, title: e.target.value }))
      }
      placeholder={
        newFinding.record_type === RECORD_TYPE.DECISION
          ? "Decision title..."
          : "Finding title..."
      }
      style={{
        padding: 10,
        borderRadius: 10,
        border: "1px solid #D7DEE9",
        fontSize: 13,
      }}
    />

    <textarea
      value={newFinding.description}
      onChange={(e) =>
        setNewFinding((prev) => ({
          ...prev,
          description: e.target.value,
        }))
      }
      placeholder={
        newFinding.record_type === RECORD_TYPE.DECISION
          ? "Decision details..."
          : "Finding description..."
      }
      rows={3}
      style={{
        padding: 10,
        borderRadius: 10,
        border: "1px solid #D7DEE9",
        fontSize: 13,
      }}
    />

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
      {newFinding.record_type === RECORD_TYPE.FINDING ? (
        <select
          value={newFinding.severity}
          onChange={(e) =>
            setNewFinding((prev) => ({
              ...prev,
              severity: e.target.value,
            }))
          }
          style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
        >
          {FINDING_SEVERITY.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      ) : (
        <input
          value={newFinding.decision_type}
          onChange={(e) =>
            setNewFinding((prev) => ({
              ...prev,
              decision_type: e.target.value,
            }))
          }
          placeholder="Decision Type"
          style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
        />
      )}

      <input
        value={newFinding.owner}
        onChange={(e) =>
          setNewFinding((prev) => ({ ...prev, owner: e.target.value }))
        }
        placeholder="Owner"
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />

      <input
        type="date"
        value={newFinding.target_date}
        onChange={(e) =>
          setNewFinding((prev) => ({
            ...prev,
            target_date: e.target.value,
          }))
        }
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />
    </div>

    {newFinding.record_type === RECORD_TYPE.DECISION ? (
      <input
        value={newFinding.approver}
        onChange={(e) =>
          setNewFinding((prev) => ({ ...prev, approver: e.target.value }))
        }
        placeholder="Approver"
        style={{ padding: 10, borderRadius: 10, border: "1px solid #D7DEE9" }}
      />
    ) : null}

    <div style={{ display: "flex", justifyContent: "flex-end" }}>
      <button
        onClick={addFinding}
        style={{
          padding: "8px 12px",
          borderRadius: 8,
          border: "none",
          background: "#1D4ED8",
          color: "#fff",
          fontWeight: 900,
          cursor: "pointer",
        }}
      >
        {newFinding.record_type === RECORD_TYPE.DECISION
          ? "Add Decision"
          : "Add Finding"}
      </button>
    </div>
  </div>
</Section>

    <Section title="Analyst Notes">
      {notes.length === 0 ? (
        <div style={{ color: "#64748B", fontSize: 13, marginBottom: 10 }}>
          No notes yet.
        </div>
      ) : (
        notes.map((n) => (
          <div
            key={n.id}
            style={{
              background: "#FFFFFF",
              border: "1px solid #E7ECF3",
              borderLeft: "4px solid #1D4ED8",
              padding: 10,
              borderRadius: 10,
              marginBottom: 8,
            }}
          >
            <div style={{ fontSize: 13, color: "#0B1F33" }}>{n.note_text}</div>
            <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>
              {n.created_by} •{" "}
              {new Date(n.created_date || n.event_timestamp).toLocaleString()}
            </div>
          </div>
        ))
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <textarea
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          placeholder="Add internal assessment note..."
          rows={2}
          style={{
            flex: 1,
            padding: 10,
            borderRadius: 10,
            border: "1px solid #D7DEE9",
            fontSize: 13,
          }}
        />
        <button
          onClick={addNote}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "none",
            background: "#027A48",
            color: "#fff",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          Add
        </button>
      </div>
    </Section>

    <Section
      title="Evidence"
      right={<Pill text={`${documents.length} attached`} tone="info" />}
    >
      {documents.length === 0 ? (
        <div style={{ color: "#64748B", fontSize: 13, marginBottom: 10 }}>
          No evidence attached.
        </div>
      ) : (
        documents.map((d) => (
          <div
            key={d.id}
            style={{
              border: "1px solid #F1F5F9",
              borderRadius: 10,
              padding: 10,
              marginBottom: 8,
              background: "#FBFDFF",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 900,
                    color: "#0B1F33",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {d.file_name}
                </div>
        
                <div
                  style={{
                    fontSize: 11,
                    color: "#64748B",
                    marginTop: 4,
                  }}
                >
                  {d.uploaded_by} •{" "}
                  {d.uploaded_at
                    ? new Date(
                        d.uploaded_at
                      ).toLocaleString()
                    : "No upload date"}
                </div>
              </div>
        
             <div
  style={{
    display: "flex",
    gap: 8,
    alignItems: "center",
  }}
>
  <button
    type="button"
    onClick={() => viewEvidence(d)}
    style={{
      padding: "6px 9px",
      borderRadius: 8,
      border: "1px solid #BFDBFE",
      background: "#EFF6FF",
      color: "#1D4ED8",
      fontWeight: 800,
      cursor: "pointer",
      whiteSpace: "nowrap",
    }}
  >
    View
  </button>

  <button
    type="button"
    onClick={() => deleteEvidence(d)}
    disabled={deletingAttachmentId === d.id}
    style={{
      padding: "6px 9px",
      borderRadius: 8,
      border: "1px solid #FECACA",
      background: "#FEF2F2",
      color: "#B91C1C",
      fontWeight: 800,
      cursor:
        deletingAttachmentId === d.id
          ? "not-allowed"
          : "pointer",
      opacity:
        deletingAttachmentId === d.id
          ? 0.6
          : 1,
      whiteSpace: "nowrap",
    }}
  >
    {deletingAttachmentId === d.id
      ? "Deleting..."
      : "Delete"}
  </button>
</div>
            </div>
          </div>
        ))
      )}

<label
  style={{
    display: "inline-block",
    padding: "8px 12px",
    borderRadius: 8,
    border: "none",
    background: "#1D4ED8",
    color: "#fff",
    fontWeight: 900,
    cursor: evidenceUploading ? "not-allowed" : "pointer",
    opacity: evidenceUploading ? 0.65 : 1,
  }}
>
  {evidenceUploading ? "Uploading..." : "Attach Evidence"}
  <input
    type="file"
    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt,.csv,.xlsx"
    disabled={evidenceUploading}
    onChange={(e) => {
      const file = e.target.files?.[0];
      if (file) attachDocument(file);
      e.target.value = "";
    }}
    style={{ display: "none" }}
  />
</label>
    </Section>

    <Section
  title="Remediation Actions"
  right={
    <Pill
      text={`${reminders.filter((x) => x.status !== "Closed").length} active`}
      tone="warn"
    />
  }
>
      {reminders.length === 0 ? (
        <div style={{ color: "#64748B", fontSize: 13, marginBottom: 10 }}>
          No action items.
        </div>
      ) : (
        reminders.map((r) => (
          <div
            key={r.id}
            style={{
              border: "1px solid #F1F5F9",
              borderRadius: 10,
              padding: 10,
              marginBottom: 8,
              background: r.status === "Closed" ? "#F8FAFC" : "#FFFFFF",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 900, color: "#0B1F33" }}>
                  {r.reminder_type}
                </div>
                <div style={{ fontSize: 11, color: "#64748B", marginTop: 4 }}>
                  {r.due_date} • {dueLabel(r.due_date)}
                </div>
              </div>
              {r.status === "Closed" ? (
                <Pill text="Closed" tone="good" />
              ) : (
                <button
                  onClick={() => closeReminder(r.id)}
                  style={{
                    padding: "5px 8px",
                    borderRadius: 8,
                    border: "1px solid #BBF7D0",
                    background: "#ECFDF3",
                    color: "#027A48",
                    fontWeight: 800,
                    cursor: "pointer",
                    height: 30,
                  }}
                >
                  Close
                </button>
              )}
            </div>
          </div>
        ))
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="number"
          min="1"
          value={newReminderDays}
          onChange={(e) => setNewReminderDays(e.target.value)}
          style={{
            width: 100,
            padding: 10,
            borderRadius: 10,
            border: "1px solid #D7DEE9",
          }}
        />
        <span style={{ fontSize: 12, color: "#64748B" }}>days from today</span>
        <button
          onClick={addReminder}
          style={{
            padding: "8px 12px",
            borderRadius: 8,
            border: "1px solid #FEDF89",
            background: "#FFFAEB",
            color: "#B54708",
            fontWeight: 900,
            cursor: "pointer",
          }}
        >
          Add Action
        </button>
      </div>
    </Section>

    <Section
      title="Activity"
      right={<Pill text={`${history.length} events`} tone="info" />}
    >
      {history.length === 0 ? (
        <div style={{ color: "#64748B", fontSize: 13 }}>No activity yet.</div>
      ) : (
        history.map((h) => (
          <div
            key={h.id}
            style={{
              padding: "8px 0",
              borderBottom: "1px solid #F1F5F9",
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 800,
                color: "#0B1F33",
              }}
            >
              {h.log_message || h.text}
            </div>
        
            <div
              style={{
                fontSize: 11,
                color: "#64748B",
                marginTop: 3,
              }}
            >
              {h.initiated_by || h.actor} •{" "}
              {h.event_timestamp &&
                h.event_timestamp !== "N/A"
                  ? new Date(h.event_timestamp).toLocaleString()
                  : "No timestamp"}
            </div>
          </div>
        ))
      )}
    </Section>
  </>
) : (
            <div style={{ color: "#64748B" }}>Select an assessment.</div>
          )}
        </div>
      </div>
    </SSBReportShell>
  );
}